import { type ReactElement, useMemo } from 'react';

import type {
  FilesFileSchemaWithId,
  FilesFileSetSchemaWithIdNullable,
  FilesFormatSchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';

import { MANAGE_SUBTITLES } from '../../constants/help_urls';
import type { FilesAssetSubtitle } from '../../ducks/files/types';
import OpenContextMenuButton from '../../ui/table/open_context_menu_button/open_context_menu_button';
import {
  DownloadOpenFileSets,
  FileSets,
  Storage,
  SubtitleName,
} from './cell_components';
import FormatsPanelContextMenu from './context_menu_formats';
import TableGroup from './table_group';
import type { FilesGridColumn, FormatProgress } from './types';

type TableSubtitlesProps = {
  items: FilesFormatSchemaWithId[];
  subtitles: { [formatId: string]: FilesAssetSubtitle };
  files: FilesFileSchemaWithId[];
  canDownload: boolean;
  assetId: string;
  onShowFilesClick: (fileSet: FilesFileSetSchemaWithIdNullable) => void;
  uploading: FormatProgress;
  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;
  readOnly?: boolean;
};

type ColumnProps = Pick<
  TableSubtitlesProps,
  | 'subtitles'
  | 'uploading'
  | 'files'
  | 'canDownload'
  | 'assetId'
  | 'readOnly'
  | 'onShowFilesClick'
>;

const getColumns = (
  props: ColumnProps,
): FilesGridColumn<FilesFormatSchemaWithId>[] => [
  {
    name: 'name',
    component: ({ item }) => (
      <SubtitleName
        format={item}
        subtitle={props.subtitles[item.id]}
        progress={props.uploading[item.id]}
      />
    ),
  },
  {
    name: 'asset_id',
    component: ({ item }) => (
      <FileSets onClick={props.onShowFilesClick} format={item} />
    ),
  },
  {
    name: 'storage_methods',
    component: ({ item }) => <Storage format={item} />,
  },
  {
    name: 'status',
    component: ({ item }) =>
      props.readOnly ? null : (
        <DownloadOpenFileSets
          canDownload={props.canDownload}
          format={item}
          files={props.files}
          assetId={props.assetId}
        />
      ),
  },
  {
    name: 'menu',
    component: () => <OpenContextMenuButton />,
  },
];

export default function TableSubtitles(
  props: TableSubtitlesProps,
): ReactElement | null {
  const {
    items,
    subtitles,
    uploading,
    files,
    canDownload,
    assetId,
    readOnly,
    onShowFilesClick,
  } = props;

  const columns = useMemo(
    () =>
      getColumns({
        subtitles,
        uploading,
        files,
        canDownload,
        assetId,
        readOnly,
        onShowFilesClick,
      }),
    [
      subtitles,
      uploading,
      files,
      canDownload,
      assetId,
      readOnly,
      onShowFilesClick,
    ],
  );

  if (!items.length) {
    return null;
  }

  return (
    <TableGroup<FilesFormatSchemaWithId>
      title="SUBTITLES"
      items={items}
      contextMenu={FormatsPanelContextMenu}
      columns={columns}
      help={MANAGE_SUBTITLES}
      uiKey="subtitles"
      selectedIds={props.selectedIds}
      onSelectIds={props.onSelectIds}
    />
  );
}
